import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework3Question.css';

const Homework3Question1: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-load sample image on component mount
  useEffect(() => {
    const loadSampleImage = async () => {
      try {
        const response = await fetch('/sample-images/HW3_page44.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          setOriginalImage(e.target?.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Failed to load sample image:', err);
      }
    };
    loadSampleImage();
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        setProcessedImage(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyHistogramStretching = async () => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework3_q1',
        {
          image: base64Image,
          operation: 'histogram_stretching'
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        }
      );

      if (response.data.success) {
        setProcessedImage(`data:image/png;base64,${response.data.processed_image}`);
      } else {
        setError(response.data.error || 'Processing failed');
      }
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hw3-question-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/3" className="back-button">
          ← Back to Homework 3
        </Link>

        <header className="question-header">
          <div className="badge">Question 1</div>
          <h1>Histogram Stretching</h1>
          <p className="subtitle">Percentile-based contrast enhancement (1%-99%)</p>
        </header>

        <div className="algorithm-info">
          <h3>Algorithm</h3>
          <p>
            This method uses a robust percentile-based approach, ignoring the darkest and brightest 1%
            of pixels as outliers. It stretches the remaining intensity range to [0, 255] for optimal contrast.
          </p>
          <div className="formula">
            I<sub>out</sub>(x,y) = (255 / (I<sub>99%</sub> - I<sub>1%</sub>)) × (I<sub>in</sub>(x,y) - I<sub>1%</sub>)
          </div>
        </div>

        <div className="controls-section">
          <div className="upload-area">
            <label htmlFor="image-upload" className="upload-label">
              <span className="upload-icon">📁</span>
              <span>Choose Image</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={applyHistogramStretching}
              disabled={!originalImage || loading}
              className="process-button"
            >
              {loading ? 'Processing...' : 'Apply Stretching'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="images-grid">
          <div className="image-panel">
            <h3>Original Image</h3>
            <div className="image-container">
              {originalImage ? (
                <img src={originalImage} alt="Original" />
              ) : (
                <div className="placeholder">Upload an image to start</div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Stretched Image</h3>
            <div className="image-container">
              {processedImage ? (
                <img src={processedImage} alt="Processed" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Processed image will appear here'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homework3Question1;
